import { PolicySection, GraphDataPoint } from "./types";
import type { MsaJobState } from "@app/features/msa/msaTypes";

/**
 * Transform MSA calculation results into graph data points
 * @param baseline - Baseline (without project) MSA job state
 * @param project - Project (with project) MSA job state
 * @returns Array of graph data points or empty array if both data sources are missing
 */
export const transformMsaToGraphData = (
  baseline: MsaJobState | null,
  project: MsaJobState | null
): GraphDataPoint[] => {
  // Return empty array if both baseline and project data are missing
  if (!baseline?.result && !project?.result) {
    return [];
  }

  const baselineResult = baseline?.result;
  const projectResult = project?.result;

  // Build the graph data from component means and aggregate
  const graphData: GraphDataPoint[] = [
    {
      category: "Land Use",
      withoutProject: baselineResult?.component_means.msa_lu ?? null,
      withProject: projectResult?.component_means.msa_lu ?? null,
      shortCategory: "LU"
    },
    {
      category: "Infrastructure",
      withoutProject: baselineResult?.component_means.msa_i ?? null,
      withProject: projectResult?.component_means.msa_i ?? null,
      shortCategory: "I"
    },
    {
      category: "Fragmentation",
      withoutProject: baselineResult?.component_means.msa_f ?? null,
      withProject: projectResult?.component_means.msa_f ?? null,
      shortCategory: "F"
    },
    {
      category: "Human Encroachment",
      withoutProject: baselineResult?.component_means.msa_he ?? null,
      withProject: projectResult?.component_means.msa_he ?? null,
      shortCategory: "HE"
    },
    {
      category: "MSA Aggregate",
      withoutProject: baselineResult?.msa_aggregate ?? null,
      withProject: projectResult?.msa_aggregate ?? null,
      shortCategory: "MSA"
    },
  ];

  return graphData;
};

/**
 * Transform MSA calculation results into policy section data
 * @param project - Project (with project) MSA job state
 * @returns Policy section data or null if data is missing
 */
export const transformMsaToPolicy = (
  project: MsaJobState | null
): PolicySection | null => {
  // Return null if either baseline or project data is missing or incomplete
  if (!project?.result) {
    return null;
  }

  // For now, set all values to 0 as the calculation method is TBD
  const policyData: PolicySection = {
    area: project?.result?.total_area_ha ?? 0,
    areaWithoutProject: 0,
    areaWithProject: 0,
    addedSocialValue: 0,
  };

  return policyData;
};

// Keep fake data for reference during development
export const fakeData: GraphDataPoint[] = [
  {
    category: "Land Use",
    withProject: 0.5988,
    withoutProject: 0.6666,
    shortCategory: "LU"
  },
  {
    category: "Infrastructure",
    withProject: 0.9204,
    withoutProject: 0.8938,
    shortCategory: "I"
  },
  {
    category: "Fragmentation",
    withProject: 0.7330,
    withoutProject: 0.7560,
    shortCategory: "F"
  },
  {
    category: "Human Encroachment",
    withProject: 0.8500,
    withoutProject: 0.9230,
    shortCategory: "HE"
  },
  {
    category: "MSA Aggregate",
    withProject: 0.3434,
    withoutProject: 0.4545,
    shortCategory: "MSA"
  },
];

export const policyData: PolicySection = {  
  area: 361,
  areaWithoutProject: 1513709,
  areaWithProject: 1513358,
  addedSocialValue: 693130,
};